import { FC, JSX, useEffect, useState } from 'react';

import {
	BUTTON_SIZES,
	BUTTON_TYPES,
	Copy,
	copyTextToClipboard,
	EvButton,
	EvCard,
	EvIconButton,
	EvLink,
	EvMainTitle,
	EvSection,
	EvSpinner,
	EvTextInput,
	EvTitle,
	useTitle
} from '@evinced-private/ui-common';

import { ContactUsButton } from 'src/components/contact-us-button/ContactUsButton';
import { getPageTitle } from 'src/helpers/AppHelper';

import { formatDate } from '../../components/common/helpers/DateFormatHelper';
import { EXTERNAL_LINKS } from '../../helpers/ExternalLinks';
import { Logger } from '../../services/Logger';
import { getServiceAccountDetails } from '../../services/ServiceAccountService';
import {
	getProductExpirationDetails,
	TenantExpirationDetails
} from '../../services/TenantsService';
import { ProductType } from '../../types/Tenant';

import './MobileSDKPage.scss';

export const MobileSDKPage: FC = () => {
	const [isLoading, setLoading] = useState(true);
	const [productExpirationDetails, setProductExpirationDetails] =
		useState<TenantExpirationDetails>();
	const [serviceAccountDetails, setServiceAccountDetails] = useState<{
		secret: string;
		id: string;
	}>();
	const { setTitle } = useTitle();
	setTitle(getPageTitle(''), 'Mobile SDK');

	useEffect(() => {
		const getDownloadLinks = async (): Promise<void> => {
			setLoading(true);
			try {
				// check token
				const expirationDate = await getProductExpirationDetails(ProductType.MOBILE_SDK);
				setProductExpirationDetails(expirationDate);
				if (!expirationDate.isExpired) {
					const serviceAccount = await getServiceAccountDetails();
					setServiceAccountDetails({ id: serviceAccount?.id, secret: serviceAccount?.secret });
				}
			} catch (err) {
				Logger.error('Error getting the download links');
			} finally {
				setLoading(false);
			}
		};
		getDownloadLinks();
	}, []);

	if (isLoading) {
		return (
			<div className="mobile-automation-page">
				<EvSpinner />
			</div>
		);
	}

	const renderDownloadMobileCardFooter = (link: string): JSX.Element => {
		return (
			<div className="mobile-automation-card-footer">
				<EvButton
					type={BUTTON_TYPES.PRIMARY}
					onClick={() => window.open(link, '_blank')}
					disabled={productExpirationDetails.isExpired}
					size={BUTTON_SIZES.BIG}
				>
					Get Started
				</EvButton>
			</div>
		);
	};

	const cards = [
		{
			title: 'Appium Java SDK',
			content: 'For iOS and Android apps',
			link: EXTERNAL_LINKS.APPIUM_SDK_DEVELOPER_DOCS_LINK
		},
		{
			title: 'Appium Python SDK',
			content: 'For iOS and Android apps',
			link: EXTERNAL_LINKS.APPIUM_PYTHON_SDK_DEVELOPER_DOCS_LINK
		},
		{
			title: 'XCUI SDK',
			content: 'For iOS apps',
			link: EXTERNAL_LINKS.XCUI_SDK_DEVELOPER_DOCS_LINK
		},
		{
			title: 'Espresso / UIAutomator SDK',
			content: 'For Android apps',
			link: EXTERNAL_LINKS.ESPRESSO_SDK_DEVELOPER_DOCS_LINK
		},
		{
			title: 'WebdriverIO SDK',
			content: 'For iOS and Android apps',
			link: EXTERNAL_LINKS.WEBDRIVERIO_MOBILE_SDK_DEVELOPER_DOCS_LINK
		}
	];

	const renderTextBoxWithCopyButton = (
		fieldDescriptionTitle: string,
		value: string
	): JSX.Element => {
		return (
			<div className="copy-key-info">
				<div className="line1">{fieldDescriptionTitle}</div>
				<div className="copy-text">
					<EvTextInput isDisabled value={value} onChange={() => null} className="copy-value" />
					<EvIconButton
						icon={Copy}
						ariaLabel="Copy to clipboard"
						onClick={() => copyTextToClipboard(value, fieldDescriptionTitle)}
					/>
				</div>
			</div>
		);
	};

	const renderKeysSection = (): JSX.Element => {
		return (
			<EvSection className="api-key-section">
				<div className="key-info">
					<EvTitle className="key-section-title" titleText="Get your API key" />
					<div className="sub-text">
						{productExpirationDetails.isLicensedForever
							? 'Your license is active'
							: `Your license will expire on ${formatDate(
									productExpirationDetails.date
									// eslint-disable-next-line no-mixed-spaces-and-tabs
								)}`}
					</div>
				</div>
				{renderTextBoxWithCopyButton('Service Account ID', serviceAccountDetails.id)}
				{renderTextBoxWithCopyButton('API Key', serviceAccountDetails.secret)}
			</EvSection>
		);
	};

	const renderExpiredNotice = (): JSX.Element => {
		return (
			<EvSection className="api-key-section trial-ended">
				<ContactUsButton
					title="Would you like to renew your license?"
					product="Automation for Mobile"
				/>
				<div className="error-message">Your license has ended</div>
			</EvSection>
		);
	};
	return (
		<div className="mobile-automation-page">
			<div className="mobile-automation-content">
				<EvMainTitle>Welcome to Evinced Test Automation SDKs for Mobile apps!</EvMainTitle>
				<p className="sub-title">
					Read installation guidelines per test framework via the links below.
					<br />
					Please note the SDK files are stored in a private JFrog repository. Contact us or your
					account admin for access.
				</p>
				<EvSection className="cards-section">
					{cards.map((cardInfo) => (
						<EvCard
							key={cardInfo.link}
							className="mobile-automation-card"
							title={cardInfo.title}
							footer={renderDownloadMobileCardFooter(cardInfo.link)}
						>
							{cardInfo.content}
						</EvCard>
					))}
				</EvSection>
				{productExpirationDetails.isExpired ? renderExpiredNotice() : renderKeysSection()}
				<div className="more-info">
					For a quick overview of how SDKs work, please visit the&nbsp;
					<EvLink
						url={EXTERNAL_LINKS.LEARN_MORE_MOBILE_AUTOMATION_LINK}
						linkText="Automation for Mobile"
						openInNewTab
					/>
					&nbsp;page at Evinced.com.
				</div>
			</div>
		</div>
	);
};
